<template>
  <div class="create-namespace-form">
    <BaseNamespaceForm v-model="baseNamespaceFormData" />
    <div class="create-namespace-form__buttons">
      <TirPmButton
        :variant="TirPmButtonVariantEnum.PrimaryState"
        :isLoading="isLoading"
        :isDisabled="!disabledCreateNamespace"
        @click="createNamespace"
      >
        Создать
      </TirPmButton>
      <TirPmButton :variant="TirPmButtonVariantEnum.SecondaryState" @click="emit('onCancel')">
        Отмена
      </TirPmButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, Ref } from 'vue';
import { BaseNamespaceForm, BaseNamespaceFormDataModel } from '../base-namespace-form';
import { CreateNamespaceModel, CreateNamespaceResultModel } from './models';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmNotificationEventBus,
  TirPmNotificationModel,
} from 'tir-components';
import { AdapterService } from './adapter';

const emit = defineEmits<{
  /**Событие отмены создания рабочей области */
  (e: 'onCancel'): void;
  /**Событие создания рабочей области */
  (e: 'onCreate', value: CreateNamespaceResultModel): void;
  /**Состояние отображение текста айки */
  (e: 'tooltipElementVisibleState', value: Boolean);
}>();

const adapter = new AdapterService();

const props = defineProps({
  /**ID проекта */
  projectId: {
    type: Number,
    required: true,
  },
});

/**Данные для создания рабочей области */
const createFormData = ref<CreateNamespaceModel>();

/**Данные для базовой формы рабочей области */
const baseNamespaceFormData = computed({
  get() {
    if (createFormData.value)
      return new BaseNamespaceFormDataModel({
        Name: createFormData.value.Name,
        IconId: createFormData.value.IconId,
        IconColorId: createFormData.value.IconColorId,
        AdminIds: createFormData.value.AdminIds,
        TeamIds: createFormData.value.TeamIds,
        IconColor: createFormData.value.IconColor,
        IconUrl: createFormData.value.IconUrl,
      });
  },
  set(value) {
    createFormData.value = new CreateNamespaceModel({
      Name: value.Name,
      IconId: value.IconId,
      IconColorId: value.IconColorId,
      AdminIds: value.AdminIds,
      TeamIds: value.TeamIds,
      IconUrl: value.IconUrl,
      IconColor: value.IconColor,
    });
  },
});

/**Состояние видимости TooltipElement */
let tooltipElementVisibleState;

/**Загрузка */
const isLoading = ref(false);

/**Нет доступа создания рабочей области */
const disabledCreateNamespace = computed(
  () => baseNamespaceFormData.value?.Name && baseNamespaceFormData.value?.IconId,
);

/**Добавить оповещение */
const addNotification = (message: string, isError: boolean = false, life?: number) => {
  TirPmNotificationEventBus.emit(
    'add',
    new TirPmNotificationModel({
      title: message,
      type: isError ? 'error' : 'success',
      group: 'general',
      life: life ?? 2000,
      closable: true,
    }),
  );
};
/**Создание неймспейса */
const createNamespace = async () => {
  isLoading.value = true;
  if (createFormData.value) {
    const resp = await adapter.createNamespace(createFormData.value, props.projectId);
    tooltipElementVisibleState = false;
    if (resp.IsSuccess) {
      emit('onCreate', resp.Value);
      addNotification('Рабочая область создана');
    } else {
      addNotification(resp.ErrorMessage, true);
    }
  } else {
    tooltipElementVisibleState = true;
  }
  emit('tooltipElementVisibleState', tooltipElementVisibleState);
  isLoading.value = false;
};
</script>

<style lang="scss" scoped>
.create-namespace-form {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  &__buttons {
    padding-top: 1.5rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
}
</style>
